import { type File, useStores } from '@littlebird/sdk';
import { useEffect, useState } from 'react';

export type HydratedFile =
  | { status: 'none'; file: undefined }
  | { status: 'loading'; file: undefined }
  | { status: 'ready'; file: File }
  | { status: 'unavailable'; file: undefined };

const ID_SEPARATOR = ',';

/** Loads files into the file store by id and reports each one's state, in the order asked for. */
export const useHydratedFiles = (fileIds: string[]): HydratedFile[] => {
  const { files: filesStore } = useStores();
  const requestedIdsKey = fileIds.join(ID_SEPARATOR);
  const [lookedUpIdsKey, setLookedUpIdsKey] = useState<string | null>(null);

  useEffect(() => {
    if (!requestedIdsKey) return;
    let cancelled = false;
    const markLookedUp = () => {
      if (!cancelled) setLookedUpIdsKey(requestedIdsKey);
    };
    filesStore
      .hydrateByIds(requestedIdsKey.split(ID_SEPARATOR))
      .then(markLookedUp, markLookedUp);
    return () => {
      cancelled = true;
    };
  }, [requestedIdsKey, filesStore]);

  const lookedUp = lookedUpIdsKey === requestedIdsKey;
  return fileIds.map((fileId) => {
    const file = filesStore.getById(fileId);
    if (file) return { status: 'ready', file };
    return lookedUp
      ? { status: 'unavailable', file: undefined }
      : { status: 'loading', file: undefined };
  });
};

/** Single-file form of `useHydratedFiles`, reporting `none` when there is no id to look up. */
export const useHydratedFile = (
  fileId: string | null | undefined
): HydratedFile => {
  const [hydrated] = useHydratedFiles(fileId ? [fileId] : []);
  return hydrated ?? { status: 'none', file: undefined };
};
